import { isNil } from 'ramda';
import { useEffect, useState } from 'react';

import { showSnackbar } from '../index';
import useForceRefresh from './useForceRefresh';
import { useLatestRef } from './useLatestRef';

import type { AxiosResponse } from 'axios';

interface UseResourceProps<T extends AxiosResponse> {
  fetcher: () => Promise<T>;
  transformer?: (response: T['data']) => any;
  errorMessage?: string;
  onError?: (error: any) => void;
  shouldFetch?: boolean;
}

/**
 * Fetches a resource once and exposes a `refresh()` trigger to fetch it again.
 *
 * Fetching is driven only by `shouldFetch` and `refresh()`. Changing what `fetcher` captures
 * (e.g. an id) does *not* refetch while the same instance stays mounted — call `refresh()`,
 * toggle `shouldFetch`, or remount. Callbacks may safely be passed inline.
 */
const useResource = <T extends AxiosResponse>({
  fetcher,
  errorMessage,
  onError,
  transformer,
  shouldFetch = true
}: UseResourceProps<T>) => {
  'use memo';

  const [resource, setResource] = useState<T['data'] | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const { timestamp, refresh } = useForceRefresh();

  // Callers commonly pass these inline (e.g. `fetcher: () => getAuxValueById(id)`), so their
  // identity changes every render. `useLatestRef` hands out one stable ref that always holds
  // the newest values, so the effect below can depend on it -- an exhaustive dependency array
  // that a fresh inline callback cannot invalidate.
  const callbacksRef = useLatestRef({
    fetcher,
    transformer,
    onError,
    errorMessage
  });

  useEffect(() => {
    if (!shouldFetch) {
      // A fetch that was in flight has just been cancelled by the cleanup, so its `.finally`
      // leaves the loading flag alone. Nothing turns it on again from here, so reset it.
      setIsFetching(false);
      return;
    }

    const { fetcher, transformer, onError, errorMessage } =
      callbacksRef.current;
    let cancelled = false;

    setIsFetching(true);
    fetcher()
      .then((response) => {
        if (cancelled) {
          return;
        }
        setResource(
          isNil(transformer) ? response.data : transformer(response.data)
        );
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        onError && onError(error);
        showSnackbar({
          severity: 'error',
          message: !isNil(errorMessage) ? errorMessage : 'Error'
        });
      })
      .finally(() => {
        if (!cancelled) {
          setIsFetching(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // Fetching is driven only by `shouldFetch` and the explicit `refresh()` trigger
    // (`timestamp`). `callbacksRef` is stable, so listing it costs nothing and keeps a new
    // inline callback identity from retriggering the fetch → setState → render loop.
  }, [callbacksRef, timestamp, shouldFetch]);

  return { resource, refresh, isFetching };
};

export default useResource;
